import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

import withLazyLoading, { carryPreload, lazyChunk } from '../../../hoc/withLazyLoading';
import { EQUIPMENT_LIBRARY_PATTERNS, EQUIPMENT_LIBRARY_ROUTES, EQUIPMENT_LIBRARY_SEGMENTS } from '../../../navigation/paths';
import { createCrumbHandle, withCrumbParents } from '../../../utilities/auth';
import type { CrumbParent } from '../../../utilities/auth';
import { EQUIPMENT_LIBRARY_RULES, access } from '../../../access';
import { libraryTab, vocabularyView } from '../../../modules/ctl-config/components/library/libraryTab';
import {
  warmLibraryType,
  warmLibraryTypes,
  warmPlantTypes,
  warmScadaVocabulary,
  warmTypeEditor,
} from '../../../modules/ctl-config/routes/routeWarmUps';
import { warmUp } from '../../warmRoutes';
import { guardedRoute } from '../guardedRoute';

import { CTL_CONFIG_FEATURE as FEATURE } from './feature';

const libraryChunk = lazyChunk(() => import('../../../modules/ctl-config/routes/libraryPages'));

const LibraryLayout = libraryChunk((m) => m.LibraryLayout);

const EquipmentTypesTab = libraryChunk((m) => m.EquipmentTypesTab);

const TypeDetailPane = libraryChunk((m) => m.TypeDetailPane);

const PlantTypesTab = libraryChunk((m) => m.PlantTypesTab);

const ScadaVocabularyTab = libraryChunk((m) => m.ScadaVocabularyTab);

const ReadingsView = libraryChunk((m) => m.ReadingsView);

const EquipmentCodesView = libraryChunk((m) => m.EquipmentCodesView);

const TypeEditRoute = withLazyLoading(() =>
  import('../../../modules/ctl-config/components/equipment-library/TypeEditRoute').then((m) => ({ default: m.TypeEditRoute })),
);

const KeyedTypeDetail: React.FC = () => {
  const { code = '' } = useParams();
  return guardedRoute(FEATURE, <TypeDetailPane key={code} code={code} />, [code]);
};

const KeyedTypeEdit: React.FC = () => {
  const { code = '' } = useParams();
  return guardedRoute(FEATURE, <TypeEditRoute key={code} code={code} />, [code]);
};

const LegacyTypeRedirect: React.FC = () => {
  const { code = '' } = useParams();
  const { search } = useLocation();
  return <Navigate to={{ pathname: EQUIPMENT_LIBRARY_ROUTES.type(code), search }} replace />;
};

const LegacyEditRedirect: React.FC = () => {
  const { code = '' } = useParams();
  const { search } = useLocation();
  return <Navigate to={{ pathname: EQUIPMENT_LIBRARY_ROUTES.typeEdit(code), search }} replace />;
};

const GuardedTypeDetail = carryPreload(KeyedTypeDetail, TypeDetailPane);

const GuardedTypeEdit = carryPreload(KeyedTypeEdit, TypeEditRoute);

const VIEW = access(EQUIPMENT_LIBRARY_RULES.view);

const MANAGE = access(EQUIPMENT_LIBRARY_RULES.manage);

const TYPES_CRUMB: CrumbParent = { label: 'Equipment types', pathname: EQUIPMENT_LIBRARY_ROUTES.types };

const TYPE_CRUMB: CrumbParent = { label: 'Equipment type', pathname: ({ code }) => EQUIPMENT_LIBRARY_ROUTES.type(code ?? '') };

const typeRoutes: RouteObject = {
  path: EQUIPMENT_LIBRARY_SEGMENTS.types,
  element: <EquipmentTypesTab />,
  handle: { ...createCrumbHandle('Equipment types'), ...VIEW, ...warmUp(warmLibraryTypes), ...libraryTab('types') },
  children: [
    {
      path: ':code',
      element: <GuardedTypeDetail />,
      handle: { ...createCrumbHandle('Equipment type'), ...VIEW, ...warmUp(warmLibraryType) },
    },
  ],
};

const plantTypeRoutes: RouteObject = {
  path: EQUIPMENT_LIBRARY_SEGMENTS.plantTypes,
  element: <PlantTypesTab />,
  handle: { ...createCrumbHandle('Plant types'), ...VIEW, ...warmUp(warmPlantTypes), ...libraryTab('plantTypes') },
};

const vocabularyRoutes: RouteObject = {
  path: EQUIPMENT_LIBRARY_SEGMENTS.scadaVocabulary,
  element: <ScadaVocabularyTab />,
  handle: { ...createCrumbHandle('SCADA vocabulary'), ...VIEW, ...warmUp(warmScadaVocabulary), ...libraryTab('scadaVocabulary') },
  children: [
    { index: true, element: <Navigate to={EQUIPMENT_LIBRARY_ROUTES.readings} replace />, handle: VIEW },
    {
      path: EQUIPMENT_LIBRARY_SEGMENTS.readings,
      element: <ReadingsView />,
      handle: { ...createCrumbHandle('Readings'), ...VIEW, ...vocabularyView('readings') },
    },
    {
      path: EQUIPMENT_LIBRARY_SEGMENTS.equipmentCodes,
      element: <EquipmentCodesView />,
      handle: { ...createCrumbHandle('Equipment codes'), ...VIEW, ...vocabularyView('equipmentCodes') },
    },
  ],
};

export const libraryRoutes: RouteObject[] = [
  {
    path: EQUIPMENT_LIBRARY_PATTERNS.root,
    handle: { ...createCrumbHandle('Equipment library'), ...VIEW, phoneBack: true },
    children: [
      { index: true, element: <Navigate to={EQUIPMENT_LIBRARY_ROUTES.types} replace />, handle: VIEW },
      {
        element: guardedRoute(FEATURE, <LibraryLayout />),
        handle: VIEW,
        children: [typeRoutes, plantTypeRoutes, vocabularyRoutes],
      },
      {
        path: `${EQUIPMENT_LIBRARY_SEGMENTS.types}/:code/${EQUIPMENT_LIBRARY_SEGMENTS.edit}`,
        element: <GuardedTypeEdit />,
        handle: { ...withCrumbParents(createCrumbHandle('Edit equipment type'), [TYPES_CRUMB, TYPE_CRUMB]), ...MANAGE, ...warmUp(warmTypeEditor) },
      },
      { path: ':code', element: <LegacyTypeRedirect />, handle: VIEW },
      { path: `:code/${EQUIPMENT_LIBRARY_SEGMENTS.edit}`, element: <LegacyEditRedirect />, handle: MANAGE },
      { path: EQUIPMENT_LIBRARY_SEGMENTS.legacyDatabase, element: <Navigate to={EQUIPMENT_LIBRARY_ROUTES.readings} replace />, handle: VIEW },
    ],
  },
];
